import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  addTourProduct,
  getSupplierProductTypes,
  getSupplierCurrencies,
  getCities,
  saveProductContentInfo,
  getContentMainList,
  createNewProductContent,
  getItineraryType,
  addItinerarySchedule,
  saveItineraryContent,
  deleteItinerarySchedule,
  deleteProductContent,
  getLandboxSimpleSchedules,
  getLandboxScheduleDetailHtml,
  getCommonImagesByCity,
  downloadCommonImagesToProduct,
  getProductGallery,
  saveRepresentativeImage,
} from '../services/apiService';
import SmartItineraryRegisterModal from '../components/SmartItineraryRegisterModal';
import { getProductDetails, saveProductBasicInfo } from '../services/productService';
import { getUserInfo } from '../utils/tokenManager';
import { getAuthInfo } from '../services/authService';

// Pull IDs strictly from the token
const getDefaultsFromToken = () => {
  const u = getUserInfo() || {};
  const userNo = Number(u.userNo);
  const companyNo = Number(u.companyNo); // 공급업체번호
  const supplierNo = companyNo; // 공급자번호 = 공급업체번호
  return { userNo, companyNo, supplierNo };
};

// Use token-derived defaults
const DEFAULTS = {
  get userNo() { return getDefaultsFromToken().userNo; },
  get companyNo() { return getDefaultsFromToken().companyNo; },
  get supplierNo() { return getDefaultsFromToken().supplierNo; },
};

const StepIndicator = ({ current, total, steps = [] }) => {
  return (
    <div className="w-full">
      <div className="flex items-center justify-center w-full">
        {steps.map((label, idx) => {
          const isCompleted = idx < current;
          const isCurrent = idx === current;

          return (
            <div key={idx} className="flex items-center">
              {/* Step Circle & Label Container */}
              <div className="relative flex flex-col items-center group">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 z-10 ${isCompleted
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : isCurrent
                      ? 'bg-white border-blue-600 text-blue-600 ring-4 ring-blue-50'
                      : 'bg-white border-gray-300 text-gray-400'
                    }`}
                >
                  {isCompleted ? (
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <span className="text-sm font-bold">{idx + 1}</span>
                  )}
                </div>
                <div className={`absolute top-12 whitespace-nowrap text-sm font-medium transition-colors duration-300 ${isCurrent ? 'text-blue-700' : isCompleted ? 'text-gray-900' : 'text-gray-400'
                  }`}>
                  {label}
                </div>
              </div>

              {/* Connector Line */}
              {idx < steps.length - 1 && (
                <div className={`w-24 h-0.5 mx-2 transition-colors duration-300 ${idx < current ? 'bg-blue-600' : 'bg-gray-200'
                  }`} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const QuickProductWizard = () => {
  const navigate = useNavigate();
  const [defaults, setDefaults] = useState(getDefaultsFromToken());
  const [step, setStep] = useState(0);
  const totalSteps = 2;
  const stepTitles = [
    '기초 정보',
    '스마트 등록',
  ];

  // Shared state
  const [productNo, setProductNo] = useState(null);
  const [autoFetchingImages, setAutoFetchingImages] = useState(false);

  // Step 1 state
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [options, setOptions] = useState({
    cities: [],
    productTypes: [],
    currencies: [],
  });
  const [form1, setForm1] = useState({
    productName: '',
    cityNo: '',
    productTypeCode: '',
    durationValue: '1',
    durationUnitCode: '012001', // 시간: 012002, 일: 012001
    currencyCode: '',
  });
  const form1Valid = useMemo(() => {
    return (
      form1.productName.trim().length > 0 &&
      String(form1.cityNo).length > 0 &&
      String(form1.productTypeCode).length > 0 &&
      String(form1.currencyCode).length > 0
    );
  }, [form1]);

  // Step 2 state (기본 설정)
  const [form2, setForm2] = useState({
    expose: true,
    keywords: '',
    productCode: '',
    badge: '',
  });
  const [savingStep2, setSavingStep2] = useState(false);
  const [smartInfoOpen, setSmartInfoOpen] = useState(false);
  const creatingProductRef = useRef(false);

  // Auto-load generated product code when entering Step 2
  useEffect(() => {
    if (!productNo) return;
    if (step !== 1) return;
    let cancelled = false;
    (async () => {
      try {
        const details = await getProductDetails(productNo, defaults.companyNo).catch(() => null);
        if (!details || cancelled) return;
        const code = details.상품코드 || details.ProductCode || '';
        const badge = details.상품뱃지 || details.badge || '';
        setForm2((prev) => {
          const next = { ...prev };
          if (!prev.productCode && code) next.productCode = String(code || '');
          if (!prev.badge && badge) next.badge = String(badge || '');
          return next;
        });
      } catch {
        // ignore failures, 입력 필드는 비워둠
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [productNo, step, defaults.companyNo]);

  // Step 3 state (대표 설명/요약)
  const [form3, setForm3] = useState({
    title: '',
    summary: '',
  });
  const [savingStep3, setSavingStep3] = useState(false);
  const [contentTypes, setContentTypes] = useState([]); // [{코드, 코드명, 예시}]
  const [selectedContentType, setSelectedContentType] = useState('');
  const [loadingStep3Options, setLoadingStep3Options] = useState(false);
  const [addedContents, setAddedContents] = useState([]); // preview list for step 3 [{contentNo, typeCode, typeName, title}]
  const [addingContent, setAddingContent] = useState(false);
  const [expandedContentNo, setExpandedContentNo] = useState(null);
  const [editingContentNo, setEditingContentNo] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', text: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  // City combobox states
  const [cityOpen, setCityOpen] = useState(false);
  const [cityQuery, setCityQuery] = useState('');
  const cityBoxRef = React.useRef(null);
  // Step 3: Smart itinerary modal state
  const [smartModalOpen, setSmartModalOpen] = useState(false);
  const smartModalRef = useRef(null);
  const [smartModalTab, setSmartModalTab] = useState('editor'); // Track which tab is active in smart modal

  // Step 4 state (일차별 일정 간단 추가)
  const [itineraryTypeNo, setItineraryTypeNo] = useState(null);
  const [form4, setForm4] = useState({
    dayNo: '1',
    planName: '',
    planContent: '',
  });
  const [savingStep4, setSavingStep4] = useState(false);
  const [createdItins, setCreatedItins] = useState([]); // preview list [{dayNo, name, content, scheduleNo}]
  const [addingItin, setAddingItin] = useState(false);
  const [editingItinScheduleNo, setEditingItinScheduleNo] = useState(null);
  const [editItinForm, setEditItinForm] = useState({ name: '', content: '' });
  const [savingEditItin, setSavingEditItin] = useState(false);
  const [deletingItinScheduleNo, setDeletingItinScheduleNo] = useState(null);

  // Step 4: Determine maximum selectable day based on Step 1 basic info (일차)
  const maxDays = useMemo(() => {
    // durationUnitCode: '012001' => 일, '012002' => 시간
    const isDays = String(form1.durationUnitCode) === '012001';
    const days = Number(form1.durationValue || 1) || 1;
    return Math.max(1, isDays ? days : 1);
  }, [form1.durationUnitCode, form1.durationValue]);

  // Step 4: itinerary edit/delete handlers
  const startEditItinerary = (it) => {
    setEditingItinScheduleNo(it.scheduleNo);
    setEditItinForm({ name: it.name || '', content: it.content || '' });
  };

  const cancelEditItinerary = () => {
    setEditingItinScheduleNo(null);
    setEditItinForm({ name: '', content: '' });
  };

  const saveEditedItinerary = async () => {
    if (!editingItinScheduleNo) return;
    if (!productNo || !itineraryTypeNo) { alert('상품 또는 일정타입 정보가 없습니다.'); return; }
    try {
      setSavingEditItin(true);
      await saveItineraryContent({
        scheduleNo: Number(editingItinScheduleNo),
        itineraryTypeNo: Number(itineraryTypeNo),
        productNo: Number(productNo),
        name: String(editItinForm.name || ''),
        content: String(editItinForm.content || ''),
        userNo: String(defaults.userNo),
      });
      setCreatedItins((prev) => prev.map((it) => (
        Number(it.scheduleNo) === Number(editingItinScheduleNo)
          ? { ...it, name: editItinForm.name || '', content: editItinForm.content || '' }
          : it
      )));
      cancelEditItinerary();
    } catch (e) {
      console.error('일정 저장 실패:', e);
      alert('일정 저장에 실패했습니다.');
    } finally {
      setSavingEditItin(false);
    }
  };

  const handleDeleteItinerary = async (it) => {
    if (!it || !it.scheduleNo) return;
    const ok = window.confirm(`Day ${it.dayNo} - "${it.name}" 일정을 삭제하시겠습니까?`);
    if (!ok) return;
    try {
      setDeletingItinScheduleNo(it.scheduleNo);
      await deleteItinerarySchedule({
        scheduleNo: Number(it.scheduleNo),
        itineraryTypeNo: Number(itineraryTypeNo),
        productNo: Number(productNo),
        userNo: String(defaults.userNo),
      });
      setCreatedItins((prev) => prev.filter((x) => Number(x.scheduleNo) !== Number(it.scheduleNo)));
      if (Number(editingItinScheduleNo) === Number(it.scheduleNo)) cancelEditItinerary();
    } catch (e) {
      console.error('일정 삭제 실패:', e);
      alert('일정 삭제에 실패했습니다.');
    } finally {
      setDeletingItinScheduleNo(null);
    }
  };

  // Step 4: Landbox import modal states
  const [landboxOpen, setLandboxOpen] = useState(false);
  const [landboxLoading, setLandboxLoading] = useState(false);
  const [landboxError, setLandboxError] = useState('');
  const [landboxList, setLandboxList] = useState([]);
  const [landboxFilters, setLandboxFilters] = useState({ no: '', name: '', price: '', city: '' });

  // Landbox modal: filtered list based on header filters
  const filteredLandboxList = React.useMemo(() => {
    const list = Array.isArray(landboxList) ? landboxList : [];
    const f = landboxFilters || {};
    const toUpper = (v) => String(v ?? '').trim().toUpperCase();
    const match = (val, q) => {
      const Q = toUpper(q);
      if (!Q) return true;
      return toUpper(val).includes(Q);
    };
    return list.filter((r) =>
      match(r.일정번호, f.no) &&
      match(r.일정명, f.name) &&
      match(r.상품가, f.price) &&
      match(r.방문도시, f.city)
    );
  }, [landboxList, landboxFilters]);

  const openLandboxModal = async () => {
    try {
      if (!productNo) { alert('상품이 아직 생성되지 않았습니다.'); return; }
      setLandboxOpen(true);
      setLandboxLoading(true);
      setLandboxError('');
      const list = await getLandboxSimpleSchedules({ favorite: 2 });
      setLandboxList(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      setLandboxError('일정 목록을 불러오지 못했습니다.');
    } finally {
      setLandboxLoading(false);
    }
  };

  // Helper: ensure we have content type code for '일정소개'
  const getItineraryIntroTypeCode = async () => {
    const list = await getContentMainList({ productNo: Number(productNo) }).catch(() => []);
    const found = (list || []).find((i) => String(i.코드명 || '').includes('일정소개')) || list[0];
    return String(found?.코드 || '');
  };

  const handlePickLandboxSchedule = async (scheduleNo) => {
    try {
      if (!productNo) { alert('상품이 아직 생성되지 않았습니다.'); return; }
      setLandboxLoading(true);
      const row = await getLandboxScheduleDetailHtml({ scheduleNo, shared: 0 });
      const htmlRaw = String(row?.내용 || '');
      // Parse body only
      let parsed = htmlRaw;
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlRaw, 'text/html');
        parsed = doc?.body ? doc.body.innerHTML : htmlRaw;
      } catch { }
      // Prepend Landbox styles/meta
      const LANDBOX_MARK = '/*__LANDBOX_STYLE_MARK__*/';
      const landboxHead = `
<style>${LANDBOX_MARK}
     /* MUI Container 및 테이블 width 강제 오버라이드 */
    * {
        max-width: 100% !important;
    }
    
    [class*="MuiContainer"] {
        width: 100% !important;
        max-width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        transform: none !important;
        left: auto !important;
        position: static !important;
    }
    
    table, .info-table, .__se_tbl {
        width: 100% !important;
        max-width: 100% !important;
        table-layout: auto !important;
        margin: 0 !important;
    }
    
    td, th {
        width: auto !important;
        max-width: none !important;
        word-break: break-word !important;
        overflow-wrap: break-word !important;
    }
    
    .product-info {
        width: 100% !important;
        max-width: 100% !important;
        overflow-x: visible !important;
    }
</style>
<meta charset="utf-8">
<meta content="width=device-width, initial-scale=1.0" name="viewport">
<title>일정표</title>
<link href="https://fonts.googleapis.com/icon?family=Material+Icons+Round"
    rel="stylesheet">
<meta content="복붙 한번이 만드는 변화, 랜드사와 여행사를 위한 스마트한 일정 공유 플랫폼 랜드박스"
    name="description">
<style>
    body {
        font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, 'Apple SD Gothic Neo', sans-serif;
        line-height: 1.6;
        color: #222222;
        background-color: #ffffff;
        overflow-x: hidden;
    }
        
    .material-icons-round {
        margin-top: 4px;
        font-size: 20px;
    }
        
    .detail-text {
        font-size: 14px;
    }
        
    /* 일정 섹션 스타일 */
    .day-section {
        margin-bottom: 30px;
        background: #fff;
        border-radius: 5px;
        overflow: hidden;
        border: 1px solid #cccccc;
    }
        
    .day-title {
        display: flex;
        justify-content: space-between;
        align-items: center;
        cursor: pointer;
        padding: 12px 18px;
        background: rgb(14, 37, 59);
        color: white;
        border-bottom: 1px solid #cccccc;
        transition: background-color 0.3s ease;
        font-size: 16px;
        font-weight: 600;
    }
        
    .day-content {
        padding: 18px;
        background: #fff;
        transition: all 0.3s ease;
    }
        
    .day-content.collapsed {
        display: none;
    }
        
    /* 아이콘 관련 스타일 */
    .toggle-icon {
        color: #000000;
        transition: transform 0.3s ease;
    }
        
    .toggle-icon.collapsed {
        transform: rotate(-180deg);
    }
        
    .icon {
        width: 24px;
        height: 24px;
        margin-right: 8px;
        flex-shrink: 0;
        background: #fff;
        z-index: 1;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #000000;
    }
        
    /* 스케줄 아이템 스타일 */
    .schedule-item {
        margin: 15px 0;
        display: flex;
        align-items: flex-start;
        padding: 8px;
        border-radius: 8px;
        transition: background-color 0.3s ease;
        position: relative;
    }
        
    .schedule-item:hover {
        background-color: #f7f7f7;
    }
        
    .schedule-item:not(:last-child)::after {
        content: '';
        position: absolute;
        left: 18px;
        top: 24px;
        bottom: -15px;
        width: 1px;
        border-left: 1px solid #cccccc;
    }
        
    .location {
        font-size: 14px;
    }
        
    /* 식사 정보 스타일 */
    .meals {
        margin-top: 18px;
        padding: 8px 15px;
        background: #f7f7f7;
        border-radius: 8px;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
    }
        
    .meal-info {
        display: flex;
        gap: 8px;
        white-space: nowrap;
        min-width: min-content;
    }
        
    .meal-item {
        background: #fff;
        padding: 4px 18px;
        border-radius: 15px;
        font-size: 13px;
        color: #222222;
        flex-shrink: 0;
        border: 1px solid #cccccc;
    }
        
    /* 정보 섹션 스타일 */
    .product-info {
        margin-bottom: 40px;
    }
        
    .info-section {
        background: #fff;
        border-radius: 12px;
        padding: 18px;
        font-size: 13px;
        margin-bottom: 18px;
        border: 1px solid #cccccc;
    }
        
    @media screen and (max-width: 768px) {
        body {
            padding: 0;
            margin: 0;
            max-width: 100%;
            overflow-x: hidden;
            background-color: white;
        }
        
        /* 일차별 일정 섹션 */
        .day-title {
            font-size: 14px;
            padding: 12px 15px;
            background-color: rgb(14, 37, 59) !important;
            color: white;
        }
        
        .day-section {
            padding: 0;
            border: none;
            margin-bottom: 18px;
        }
        
        .day-content {
            padding: 0;
        }
        
        .schedule-item {
            padding: 0;
        }
        
        .schedule-item:not(:last-child)::after {
            left: 8px;
        }
    
       
        .meals {
            margin-top: 15px;
            padding: 8px;
        }
        
        .meal-info {
            font-size: 12px;
            gap: 8px;
        }
        
        .meal-item {
            padding: 4px 8px;
            font-size: 11px;
        }
        
        /* 정보 섹션 모바일 최적화 */
        .info-section {
            padding: 12px;
            background-color: #fff;
            border: 1px solid #cccccc;
            margin-bottom: 12px;
        }
        
        /* 상세 텍스트 모바일 최적화 */
        .detail-text {
            font-size: 13px;
            line-height: 1.4;
            word-break: keep-all;
            overflow-wrap: break-word;
        }
        
        .location {
            font-size: 13px;
            font-weight: 600;
        }
        
        /* 아이콘 크기 조정 */
        .material-icons-round {
            font-size: 18px;
        }
        
        .icon {
            width: 20px;
            height: 20px;
            margin-right: 8px;
        }
        
        /* 테이블 가로 스크롤 방지 */
        .product-info {
            overflow-x: auto;
            margin-bottom: 18px;
        }
        
        /* 이미지 반응형 처리 */
        .product-info img {
            max-width: 100% !important;
            width: auto !important;
            height: auto !important;
        }
    }
</style>`;
      const combined = parsed.includes(LANDBOX_MARK) ? parsed : `${landboxHead}\n${parsed}`;

      // Create new content under '일정소개' with title '일정표'
      const typeCode = await getItineraryIntroTypeCode();
      if (!typeCode) { alert('컨텐츠 종류(일정소개)를 확인하지 못했습니다.'); return; }
      const title = '일정표';
      const created = await createNewProductContent({
        productNo: Number(productNo),
        companyNo: String(defaults.companyNo),
        userNo: String(defaults.userNo),
        contentTypeCode: String(typeCode),
        title,
      });
      let contentNo = null;
      if (created && Array.isArray(created.table) && created.table.length) contentNo = created.table[0].컨텐츠번호 || created.table[0].contentNo || created.table[0].번호;
      if (!contentNo && created && created.table1 && Array.isArray(created.table1) && created.table1.length) contentNo = created.table1[0].컨텐츠번호 || created.table1[0].contentNo || created.table1[0].번호;
      if (!contentNo) throw new Error('컨텐츠번호를 확인하지 못했습니다.');
      await saveProductContentInfo({
        productNo: Number(productNo),
        contentNo: Number(contentNo),
        companyNo: String(defaults.companyNo),
        userNo: String(defaults.userNo),
        title,
        text: '',
        html: combined,
        expose: true,
        dealerOnly: false,
        imageMappingUse: true,
        fileAttach: false,
      });
      setLandboxOpen(false);
      // 일정표까지 준비가 끝났으므로 바로 가격/재고 화면으로 이동
      if (productNo) {
        navigate(`/price-stock/${productNo}`);
      }
    } catch (e) {
      console.error(e);
      alert('일정표를 저장하는 중 오류가 발생했습니다.');
    } finally {
      setLandboxLoading(false);
    }
  };

  // Helpers for Korean initials and normalization (ported from NewProductModal)
  const CHO = ['ㄱ', 'ㄲ', 'ㄴ', 'ㄷ', 'ㄸ', 'ㄹ', 'ㅁ', 'ㅂ', 'ㅃ', 'ㅅ', 'ㅆ', 'ㅇ', 'ㅈ', 'ㅉ', 'ㅊ', 'ㅋ', 'ㅌ', 'ㅍ', 'ㅎ'];
  const HANGUL_BASE = 0xAC00; // '가'
  const CHOSUNG = 21 * 28;
  const getInitials = (str) => {
    if (!str) return '';
    let res = '';
    for (let ch of String(str)) {
      const code = ch.charCodeAt(0);
      if (code >= 0xAC00 && code <= 0xD7A3) {
        const idx = Math.floor((code - HANGUL_BASE) / CHOSUNG);
        res += CHO[idx] || ch;
      } else {
        res += ch.toUpperCase();
      }
    }
    return res;
  };

  const handleDeleteContent = async (contentNo) => {
    if (!contentNo) return;
    const ok = window.confirm('이 컨텐츠를 삭제하시겠습니까? 삭제 후 복구할 수 없습니다.');
    if (!ok) return;
    try {
      await deleteProductContent({ contentNo: Number(contentNo), userNo: String(defaults.userNo) });
      setAddedContents((prev) => prev.filter((c) => String(c.contentNo) !== String(contentNo)));
      if (String(expandedContentNo) === String(contentNo)) setExpandedContentNo(null);
    } catch (e) {
      console.error('컨텐츠 삭제 실패:', e);
      alert('컨텐츠 삭제에 실패했습니다.');
    }
  };

  // Inline edit handlers for Step 3 contents
  const startEditContent = (c) => {
    setEditingContentNo(c.contentNo);
    setExpandedContentNo(c.contentNo);
    setEditForm({ title: c.title || '', text: c.text || '' });
  };

  const cancelEditContent = () => {
    setEditingContentNo(null);
    setEditForm({ title: '', text: '' });
  };

  const saveEditContent = async () => {
    if (!editingContentNo) return;
    try {
      setSavingEdit(true);
      const htmlFromText = (() => {
        const t = String(editForm.text || '').trim();
        if (!t) return '';
        // Basic HTML conversion: preserve newlines as <br/>
        const escaped = t
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
        const withBr = escaped.replace(/\n/g, '<br/>');
        return `<div class="detail-text">${withBr}</div>`;
      })();
      await saveProductContentInfo({
        productNo: Number(productNo),
        contentNo: Number(editingContentNo),
        companyNo: String(defaults.companyNo),
        userNo: String(defaults.userNo),
        title: editForm.title || '',
        text: editForm.text || '',
        html: htmlFromText,
        expose: true,
        dealerOnly: false,
        imageMappingUse: true,
        fileAttach: false,
      });
      setAddedContents((prev) => prev.map((c) => (
        String(c.contentNo) === String(editingContentNo)
          ? { ...c, title: editForm.title || '', text: editForm.text || '' }
          : c
      )));
      setEditingContentNo(null);
      setEditForm({ title: '', text: '' });
    } catch (e) {
      console.error('컨텐츠 수정 실패:', e);
      alert('컨텐츠 수정에 실패했습니다.');
    } finally {
      setSavingEdit(false);
    }
  };

  // Add one itinerary item (used in Step 4)
  const handleAddItinerary = async () => {
    if (!productNo) { alert('상품이 아직 생성되지 않았습니다.'); return; }
    if (!itineraryTypeNo) { alert('일정타입 정보를 불러오지 못했습니다.'); return; }
    try {
      setAddingItin(true);
      const selectedDayRaw = Number(form4.dayNo || 1);
      const day = Math.min(Math.max(1, selectedDayRaw), maxDays);
      if (selectedDayRaw !== day) {
        // If user-entered day exceeds bounds, correct it and block add with a message
        setForm4((prev) => ({ ...prev, dayNo: String(day) }));
        alert(`일차는 기초정보에서 설정한 최대 ${maxDays}일을 초과할 수 없습니다.`);
        return;
      }
      const name = String(form4.planName || `Day ${day} 일정`);
      const content = String(form4.planContent || '');
      // 1) 일정 추가하여 상품일정번호 확보
      const created = await addItinerarySchedule({
        productNo: Number(productNo),
        itineraryTypeNo: Number(itineraryTypeNo),
        day,
        productTypeCode: '038001',
        name,
        selectedScheduleNo: undefined,
      });
      let scheduleNo = null;
      if (created && Array.isArray(created.table) && created.table.length) {
        const row = created.table[0];
        scheduleNo = row.상품일정번호 || row.scheduleNo || row.번호 || null;
      }
      if (!scheduleNo && created && Array.isArray(created.table1) && created.table1.length) {
        const row = created.table1[0];
        scheduleNo = row.상품일정번호 || row.scheduleNo || row.번호 || null;
      }
      if (!scheduleNo) throw new Error('상품일정번호를 확인하지 못했습니다.');
      // 2) 일정 내용 저장
      await saveItineraryContent({
        scheduleNo: Number(scheduleNo),
        itineraryTypeNo: Number(itineraryTypeNo),
        productNo: Number(productNo),
        name,
        content,
        userNo: String(defaults.userNo),
      });
      // 3) Preview list update and prepare next entry
      setCreatedItins((prev) => [...prev, { dayNo: day, name, content, scheduleNo }]);
      setForm4((prev) => ({
        dayNo: String(Math.min(day + 1, maxDays)),
        planName: '',
        planContent: '',
      }));
    } catch (e) {
      console.error('일정 추가 실패:', e);
      alert('일정 추가에 실패했습니다. 값을 확인해주세요.');
    } finally {
      setAddingItin(false);
    }
  };

  const normalizeKo = (s) => String(s || '')
    .replace(/[\p{Z}\s.,/\\_|:;\-]+/gu, '') // remove common separators/spaces
    .replace(/(시|군|구|도|현)$/u, '')
    .toUpperCase();

  // Analyze product name to suggest city/duration/type (called on blur/Enter)
  const analyzeNameAndSuggest = (nameInput) => {
    const name = String((nameInput ?? form1.productName) || '').trim();
    if (!name) return;

    // Suggest duration from patterns like "N박 M일", "M일", "H시간"
    try {
      const nm = name.match(/(\d+)\s*박\s*(\d+)\s*일/);
      if (nm) {
        const days = Number(nm[2]);
        const isDefaultDuration = String(form1.durationUnitCode) === '012001' && String(form1.durationValue) === '1';
        if (days && isDefaultDuration) {
          setForm1((prev) => ({ ...prev, durationUnitCode: '012001', durationValue: String(days) }));
        }
      } else {
        const d = name.match(/(\d+)\s*일/);
        if (d && !/(박)/.test(name)) {
          const days = Number(d[1]);
          if (days) {
            const isDefaultDuration = String(form1.durationUnitCode) === '012001' && String(form1.durationValue) === '1';
            if (isDefaultDuration) {
              setForm1((prev) => ({ ...prev, durationUnitCode: '012001', durationValue: String(days) }));
            }
          }
        } else {
          const h = name.match(/(\d+)\s*시간/);
          if (h) {
            const hours = Number(h[1]);
            if (hours) {
              const isDefaultDuration = String(form1.durationUnitCode) === '012001' && String(form1.durationValue) === '1';
              if (isDefaultDuration) {
                setForm1((prev) => ({ ...prev, durationUnitCode: '012002', durationValue: String(hours) }));
              }
            }
          }
        }
      }
    } catch (_) {
      // ignore parse failures
    }

    // Suggest city from name if not already selected
    if (!form1.cityNo && Array.isArray(options.cities) && options.cities.length > 0) {
      const nameRaw = name;
      const nameUpper = nameRaw.toUpperCase();
      const nameNoSpace = nameUpper.replace(/\s+/g, '');
      const nameInitials = getInitials(nameRaw);
      const nameInitialsNoSpace = nameInitials.replace(/\s+/g, '');
      const nameNorm = normalizeKo(nameRaw);
      const nameTokens = String(nameRaw)
        .split(/[^\p{L}\p{N}]+/u)
        .filter(Boolean);
      const nameTokenSet = new Set(nameTokens.map((t) => t.toUpperCase()));
      const nameTokenInitials = nameTokens.map((t) => getInitials(t));

      const scoreMatch = (label) => {
        const lab = String(label || '');
        if (!lab) return -1;
        const labUpper = lab.toUpperCase();
        const labNoSpace = labUpper.replace(/\s+/g, '');
        const labInitials = getInitials(lab);
        const labInitialsNoSpace = labInitials.replace(/\s+/g, '');
        const labNorm = normalizeKo(lab);

        let score = -1;
        if (nameUpper === labUpper || nameNoSpace === labNoSpace) score = Math.max(score, 1000 + lab.length);
        if (nameUpper.includes(labUpper)) score = Math.max(score, 800 + lab.length);
        if (nameNoSpace.includes(labNoSpace)) score = Math.max(score, 700 + lab.length);
        if (nameNorm && labNorm) {
          if (nameNorm.includes(labNorm)) score = Math.max(score, 760 + labNorm.length);
          if (labNorm.includes(nameNorm)) score = Math.max(score, 745 + labNorm.length);
        }
        for (const tok of nameTokenSet) {
          if (tok.length < 2) continue;
          if (labUpper.includes(tok)) score = Math.max(score, 720 + tok.length);
          if (tok.includes(labUpper)) score = Math.max(score, 710 + labUpper.length);
        }
        if (nameInitials === labInitials) score = Math.max(score, 600 + lab.length);
        if (nameInitials.startsWith(labInitials)) score = Math.max(score, 550 + lab.length);
        if (nameInitials.includes(labInitials)) score = Math.max(score, 500 + lab.length);
        if (nameInitialsNoSpace.includes(labInitialsNoSpace)) score = Math.max(score, 480 + lab.length);
        for (const tokInit of nameTokenInitials) {
          if (!tokInit) continue;
          if (tokInit === labInitials) score = Math.max(score, 605 + lab.length);
          if (tokInit.startsWith(labInitials)) score = Math.max(score, 545 + lab.length);
          if (labInitials.startsWith(tokInit)) score = Math.max(score, 540 + tokInit.length);
          if (tokInit.includes(labInitials) || labInitials.includes(tokInit)) score = Math.max(score, 530 + Math.min(tokInit.length, labInitials.length));
        }
        return score;
      };

      let best = null;
      let bestScore = -1;
      for (const opt of options.cities) {
        const label = String(opt.코드명 || opt.name || '');
        const s = scoreMatch(label);
        if (s > bestScore) { best = opt; bestScore = s; }
      }
      if (best && bestScore >= 470) {
        setForm1((prev) => ({ ...prev, cityNo: String(best.코드 || best.code) }));
      }
    }

    // Suggest product type
    analyzeNameAndSuggestType(name);
  };

  const prevNumberEq = (v, n) => {
    const a = Number(v);
    const b = Number(n);
    return Number.isFinite(a) && Number.isFinite(b) && a === b;
  };

  // Filtered cities (search) with ranking similar to NewProductModal
  const filteredCities = React.useMemo(() => {
    const list = Array.isArray(options.cities) ? options.cities : [];
    const q = (cityQuery || '').trim();
    if (!q) return list;
    const qUpper = q.toUpperCase();
    const qCho = getInitials(q);

    const rank = (label) => {
      const lab = String(label || '');
      if (!lab) return -1;
      const labUpper = lab.toUpperCase();
      const labNoSpace = labUpper.replace(/\s+/g, '');
      const qNoSpace = qUpper.replace(/\s+/g, '');
      const labInitials = getInitials(lab);
      const labInitialsNoSpace = labInitials.replace(/\s+/g, '');
      const labNorm = normalizeKo(lab);
      const qNorm = normalizeKo(q);

      let score = -1;
      if (labUpper === qUpper || labNoSpace === qNoSpace) score = Math.max(score, 1000 + lab.length);
      if (labUpper.includes(qUpper)) score = Math.max(score, 800 + q.length);
      if (labNoSpace.includes(qNoSpace)) score = Math.max(score, 700 + q.length);
      if (qNorm && labNorm && labNorm.includes(qNorm)) score = Math.max(score, 650 + qNorm.length);
      if (qCho && (labInitials === qCho || labInitialsNoSpace.includes(qCho.replace(/\s+/g, '')))) {
        score = Math.max(score, 600 + qCho.length);
      }
      return score;
    };

    return [...list]
      .map((opt) => ({ opt, score: rank(opt.코드명 || opt.name || '') }))
      .filter((x) => x.score >= 0)
      .sort((a, b) => b.score - a.score)
      .map((x) => x.opt);
  }, [options.cities, cityQuery]);

  // Auto-analyze on typing (debounced) to fill city/duration/type
  useEffect(() => {
    if (step !== 0) return;
    const name = String(form1.productName || '').trim();
    if (!name) return;
    const t = setTimeout(() => {
      analyzeNameAndSuggest(name);
    }, 400);
    return () => clearTimeout(t);
  }, [step, form1.productName, options.cities, options.productTypes]);

  // Suggest product type by keywords against available productTypes
  const analyzeNameAndSuggestType = (nameInput) => {
    const name = String((nameInput ?? form1.productName) || '').trim();
    if (!name) return;
    const types = options.productTypes || [];
    if (!Array.isArray(types) || types.length === 0) return;

    const upper = name.toUpperCase();
    // Define keyword groups to help ranking
    const hints = [
      { key: '패키지', words: ['패키지', '일정', '단체', '투어패키지'] },
      { key: '자유여행', words: ['자유', '프리', 'free'] },
      { key: '입장권', words: ['입장권', '티켓', 'ticket', 'pass', '패스'] },
      { key: '시티투어', words: ['시티투어', 'city tour', '시티', '투어'] },
      { key: '렌터카', words: ['렌터카', '렌트카', 'car', '렌트'] },
      { key: '액티비티', words: ['액티비티', 'activity', '체험'] },
      { key: '에어텔', words: ['에어텔'] },
      { key: '크루즈', words: ['크루즈'] },
    ];

    const scoreType = (label) => {
      const lab = String(label || '');
      const labUpper = lab.toUpperCase();
      let score = 0;
      // base if label appears in name
      if (upper.includes(labUpper)) score += 50 + lab.length;
      // keywords boost
      for (const h of hints) {
        if (lab.includes(h.key)) {
          for (const w of h.words) {
            if (upper.includes(w.toUpperCase())) score += 30 + Math.min(10, w.length);
          }
        }
      }
      // Penalty: avoid picking '세미' 패키지 unless name contains 세미/SEMI
      const nameHasSemi = /세미|SEMI/i.test(upper);
      if (/세미/.test(lab) && !nameHasSemi) {
        score -= 40; // push down semi unless explicitly requested
      }
      return score;
    };

    let best = null;
    let bestScore = -1;
    for (const t of types) {
      const label = String(t.코드명 || t.name || '');
      const s = scoreType(label);
      if (s > bestScore) { best = t; bestScore = s; }
    }
    // Only override if a meaningful score and either no selection yet
    // or current selection is the default '패키지'
    const isDefaultPackage = (() => {
      const current = (types || []).find((t) => String(t.코드) === String(form1.productTypeCode));
      return (current && String(current.코드명 || '').includes('패키지')) || !current;
    })();
    if (best && bestScore >= 60 && isDefaultPackage) {
      const bestLabel = String(best.코드명 || best.name || '');
      const nameHasSemi = /세미|SEMI/i.test(name);
      const current = (types || []).find((t) => String(t.코드) === String(form1.productTypeCode));
      const currentIsNonSemi = current && !/세미/.test(String(current.코드명 || current.name || ''));
      if (/세미/.test(bestLabel) && !nameHasSemi && currentIsNonSemi) {
        // Do not downgrade to '세미' if current is a non-세미 selection
        return;
      }
      setForm1((prev) => ({ ...prev, productTypeCode: String(best.코드 || best.code) }));
    }
  };

  // Re-run analysis when city options load and user already typed a name (do not override if city already set)
  useEffect(() => {
    if (!form1.productName || form1.cityNo) return;
    if (!Array.isArray(options.cities) || options.cities.length === 0) return;
    analyzeNameAndSuggest();
  }, [options.cities]);

  // Guard: ensure token-derived IDs exist; attempt fallback instead of forcing login redirect
  useEffect(() => {
    const d = getDefaultsFromToken();
    let next = { ...d };
    if (!next.companyNo) {
      try {
        const info = getAuthInfo && getAuthInfo();
        const custNo = info && (info.custNo || info.CustNo);
        if (custNo) {
          next.companyNo = Number(custNo);
          next.supplierNo = Number(custNo);
        }
      } catch (_) { }
    }
    setDefaults(next);
    const stillMissing = !next.userNo || !next.companyNo;
    if (stillMissing) {
      console.warn('QuickProductWizard: 토큰 정보가 불완전합니다. userNo/companyNo 확인 필요.');
      alert('로그인 정보 확인이 필요합니다. 페이지를 새로고침하거나 다시 로그인해 주세요.');
    }
  }, []);

  // Load options for Step 1
  useEffect(() => {
    const loadOptions = async () => {
      if (!defaults.companyNo) return;
      try {
        setLoadingOptions(true);
        const [cities, productTypes, currencies] = await Promise.all([
          getCities().catch(() => []),
          getSupplierProductTypes({ companyNo: defaults.companyNo }).catch(() => []),
          getSupplierCurrencies({ companyNo: defaults.companyNo }).catch(() => []),
        ]);
        setOptions({
          cities: Array.isArray(cities) ? cities : [],
          productTypes: Array.isArray(productTypes) ? productTypes : [],
          currencies: Array.isArray(currencies) ? currencies : [],
        });
        // Set default currency to KRW if available, else first
        const currArr = Array.isArray(currencies) ? currencies : [];
        const krw = currArr.find((c) => String(c.코드명 || c.name || '').toUpperCase() === 'KRW') || currArr[0];
        if (krw && !form1.currencyCode) {
          setForm1((prev) => ({ ...prev, currencyCode: String(krw.코드 || krw.code || prev.currencyCode) }));
        }
        // Set default product type preference order:
        // 1) code === '309200' (known 패키지 코드) if exists
        // 2) exact '패키지'
        // 3) contains '패키지' but NOT '세미'
        // 4) first non-'세미'
        // 5) absolute first
        const typesArr = Array.isArray(productTypes) ? productTypes : [];
        const normalize = (s) => String(s || '').replace(/\s+/g, '');
        const byCode = typesArr.find((t) => String(t.코드 || t.code) === '309200');
        const exactPkg = typesArr.find((t) => normalize(t.코드명 || t.name) === '패키지');
        const purePkg = typesArr.find((t) => {
          const nm = String(t.코드명 || t.name || '');
          return nm.includes('패키지') && !nm.includes('세미');
        });
        const firstNonSemi = typesArr.find((t) => !String(t.코드명 || t.name || '').includes('세미'));
        const fallback = typesArr[0];
        const chosen = byCode || exactPkg || purePkg || firstNonSemi || fallback;
        if (chosen) {
          setForm1((prev) => ({ ...prev, productTypeCode: String(chosen.코드 || chosen.code || prev.productTypeCode) }));
        }
      } finally {
        setLoadingOptions(false);
      }
    };
    loadOptions();
  }, [defaults.companyNo]);

  // Close city dropdown on outside click or Escape key
  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!cityOpen) return;
      const el = cityBoxRef.current;
      if (!el) return;
      if (!el.contains(e.target)) {
        setCityOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (!cityOpen) return;
      if (e.key === 'Escape') setCityOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [cityOpen]);

  // Re-run type analysis when product types load and user already typed a name
  useEffect(() => {
    if (!form1.productName) return;
    if (!Array.isArray(options.productTypes) || options.productTypes.length === 0) return;
    analyzeNameAndSuggestType(form1.productName);
    // eslint rule not configured in project; keep deps minimal
  }, [options.productTypes]);

  // Load content types for Step 3 after productNo exists
  useEffect(() => {
    const loadContentTypes = async () => {
      if (!productNo) return;
      try {
        setLoadingStep3Options(true);
        const list = await getContentMainList({ productNo: Number(productNo) }).catch(() => []);
        setContentTypes(list);
        // default select '판매정보' if present, else '투어소개', else first
        const defaultItem =
          list.find((i) => (i.코드명 || '').includes('판매정보')) ||
          list.find((i) => (i.코드명 || '').includes('투어소개')) ||
          list[0];
        setSelectedContentType(defaultItem?.코드 || '');
      } finally {
        setLoadingStep3Options(false);
      }
    };
    loadContentTypes();
  }, [productNo]);

  // Save a single content entry (used in Step 3)
  const saveOneContent = async () => {
    // Ensure we have the basics
    const typeCode = selectedContentType || (contentTypes[0]?.코드 || '');
    if (!productNo) throw new Error('상품번호가 없습니다.');
    if (!typeCode) throw new Error('컨텐츠 종류를 선택해주세요.');
    const title = form3.title || '상품 소개';
    const text = form3.summary || '';
    const htmlFromText = (() => {
      const t = String(text || '').trim();
      if (!t) return '';
      const escaped = t
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
      const withBr = escaped.replace(/\n/g, '<br/>');
      return `<div class="detail-text">${withBr}</div>`;
    })();

    // 1) 콘텐츠 신규 생성 -> 컨텐츠번호 확보
    const created = await createNewProductContent({
      productNo: Number(productNo),
      companyNo: String(defaults.companyNo),
      userNo: String(defaults.userNo),
      contentTypeCode: String(typeCode),
      title,
    });
    let contentNo = null;
    if (created && Array.isArray(created.table) && created.table.length) {
      const row = created.table[0];
      contentNo = row.컨텐츠번호 || row.contentNo || row.번호 || null;
    }
    if (!contentNo && created && created.table1 && Array.isArray(created.table1) && created.table1.length) {
      const row = created.table1[0];
      contentNo = row.컨텐츠번호 || row.contentNo || row.번호 || null;
    }
    if (!contentNo) throw new Error('컨텐츠번호를 확인하지 못했습니다.');
    // 2) Save content info (title + summary + html derived from summary)
    await saveProductContentInfo({
      productNo: Number(productNo),
      contentNo: Number(contentNo),
      companyNo: String(defaults.companyNo),
      userNo: String(defaults.userNo),
      title,
      text,
      html: htmlFromText,
      expose: true,
      dealerOnly: false,
      imageMappingUse: true,
      fileAttach: false,
    });

    // 3) Update preview list and clear input for next entry
    const typeName = (contentTypes.find((c) => String(c.코드) === String(typeCode))?.코드명) || '';
    setAddedContents((prev) => [...prev, { contentNo, typeCode: String(typeCode), typeName, title, text }]);
    setForm3({ title: '', summary: '' });

    return { contentNo, typeCode: String(typeCode), typeName, title, text };
  };

  const handleAddContent = async () => {
    try {
      setAddingContent(true);
      const hasInput = (form3.title || '').trim().length > 0 || (form3.summary || '').trim().length > 0;
      if (!hasInput) {
        alert('제목 또는 요약을 입력한 후 추가해주세요.');
        return;
      }
      await saveOneContent();
    } catch (e) {
      console.error('컨텐츠 추가 실패:', e);
      alert('컨텐츠 추가에 실패했습니다. 값을 확인해주세요.');
    } finally {
      setAddingContent(false);
    }
  };

  // Auto-suggest Step 4 fields based on Step 1 when entering Step 4 (step === 3)
  useEffect(() => {
    if (step !== 3) return;
    // Only suggest if empty
    if (form4.planName && form4.planContent) return;
    const cityItem = (options.cities || []).find(c => String(c.코드 || c.code) === String(form1.cityNo));
    const cityName = cityItem?.코드명 || cityItem?.name || '';
    const name = (form1.productName || '').trim();
    const day = Number(form4.dayNo || 1);
    const suggestedName = name ? `${cityName ? cityName + ' ' : ''}Day ${day} 하이라이트` : `${cityName || '주요'} 일정`;
    // Extract a short pitch from product name if possible
    let pitch = '';
    const pitchMatch = name.match(/(.{0,30}?)(투어|패키지|핵심|하이라이트)/);
    if (pitchMatch) {
      pitch = `${pitchMatch[0]} 중심으로 진행됩니다.`;
    } else if (cityName) {
      pitch = `${cityName}의 핵심 스팟을 중심으로 진행됩니다.`;
    }
    setForm4(prev => ({
      ...prev,
      planName: prev.planName || suggestedName,
      planContent: prev.planContent || pitch,
    }));
  }, [step]);

  // Load itinerary type number for Step 4 once productNo exists
  useEffect(() => {
    const loadItinType = async () => {
      if (!productNo) return;
      try {
        const data = await getItineraryType({ productNo: Number(productNo) }).catch(() => null);
        // Expect data.table[0].일정타입번호
        let typeNo = null;
        if (data && Array.isArray(data.table) && data.table.length) {
          typeNo = data.table[0].일정타입번호 || data.table[0].타입번호 || data.table[0].itineraryTypeNo || null;
        }
        if (typeNo) setItineraryTypeNo(Number(typeNo));
      } catch { }
    };
    loadItinType();
  }, [productNo]);

  // Save Step 2 basic settings (expose, keywords, etc.)
  const saveStep2BasicSettings = async () => {
    if (!productNo) {
      setStep(0);
      return;
    }
    try {
      setSavingStep2(true);

      // Resolve names from loaded option lists
      const cityItem = (options.cities || []).find(c => String(c.코드 || c.code) === String(form1.cityNo));
      const productTypeItem = (options.productTypes || []).find(t => String(t.코드 || t.code) === String(form1.productTypeCode));
      const currencyItem = (options.currencies || []).find(cur => String(cur.코드 || cur.code) === String(form1.currencyCode));

      // 담당자 정보 from token
      const u = getUserInfo() || {};
      const 담당표시명 = u.email || `User-${defaults.userNo}`;
      const 담당번호 = Number(u.userNo || defaults.userNo);

      // Duration mapping
      const 소요일단위 = form1.durationUnitCode === '012001' ? '일' : '시간';

      // Keywords array
      const keywordArr = (String(form2.keywords || '')
        .split(',')
        .map(k => k.trim())
        .filter(Boolean)) || [];

      const basicInfo = {
        상품번호: Number(productNo),
        상품명: String(form1.productName || ''),
        도시번호: Number(form1.cityNo),
        도시: String(cityItem?.코드명 || cityItem?.name || ''),
        상품코드: String(form2.productCode || ''),
        상품타입: String(productTypeItem?.코드명 || productTypeItem?.name || ''),
        상품타입코드: String(form1.productTypeCode || ''),
        상품코드룰사용: !form2.productCode,
        상품담당: 담당표시명,
        상품담당번호: 담당번호,
        예약담당: 담당표시명,
        예약담당번호: 담당번호,
        디폴트수배담당: '예약담당',
        소요타입: String(form1.durationUnitCode || ''),
        소요: 소요일단위,
        소요시간: Number(form1.durationValue || 0),
        통화코드: String(form1.currencyCode || ''),
        통화: String(currencyItem?.코드명 || currencyItem?.name || ''),
        가이드타입: '한국인',
        가이드타입코드: '098001',
        카테고리: String(cityItem?.코드명 || cityItem?.name || ''),
        카테고리코드: String(cityItem?.코드명 || cityItem?.name || ''),
        노출여부: !!form2.expose,
        키워드: keywordArr,
        상품뱃지: String(form2.badge || ''),
      };

      await saveProductBasicInfo({
        companyNo: defaults.companyNo,
        userNo: defaults.userNo,
        productNo: Number(productNo),
        basicInfo,
        keywords: keywordArr.join(','),
      });
    } catch (e) {
      console.error('기본 설정 저장 실패:', e);
      alert('기본 설정 저장에 실패했습니다. 다시 시도해주세요.');
      throw e;
    } finally {
      setSavingStep2(false);
    }
  };

  // Auto-register up to 10 random representative images based on city
  const autoSetupRepresentativeImages = async () => {
    if (!productNo || !form1.cityNo) return;
    try {
      setAutoFetchingImages(true);
      const cityNoNum = Number(form1.cityNo);
      if (!cityNoNum) return;

      // 1) Load common images for this city
      const commonList = await getCommonImagesByCity({ cityNo: cityNoNum }).catch(() => []);
      const serverImgs = Array.isArray(commonList) ? commonList : [];
      if (!serverImgs.length) return;

      // 2) Pick up to 10 random images
      const shuffled = [...serverImgs].sort(() => Math.random() - 0.5);
      const picked = shuffled.slice(0, 10);
      const imageNos = picked
        .map((img) => img.이미지번호 || img.imageNo)
        .filter((n) => n !== undefined && n !== null);
      if (!imageNos.length) return;

      const companyNo = defaults.companyNo;
      const userNo = defaults.userNo;

      // 3) 서버 공용이미지 -> 상품 갤러리로 복사
      await downloadCommonImagesToProduct({
        companyNo,
        userNo,
        productNo: Number(productNo),
        imageNos,
      });

      // 4) 상품 갤러리 조회 후 복사된 이미지를 대표이미지로 등록
      // NOTE: 공용이미지(WSP_S_공용이미지)와 갤러리(RCT_S_상품갤러리)의 이미지경로2 URL 규칙이
      // 달라 경로 매칭이 실패할 수 있음. 신규 상품은 방금 복사된 갤러리 이미지를 그대로 사용.
      const gallery = await getProductGallery({ companyNo, productNo: Number(productNo) }).catch(() => []);
      if (!Array.isArray(gallery) || !gallery.length) return;

      const toSave = gallery.slice(0, Math.min(10, gallery.length));
      for (const g of toSave) {
        const imageNo = g.이미지번호 || g.ImageNo;
        if (!imageNo) continue;
        await saveRepresentativeImage({
          imageNo,
          productNo: Number(productNo),
          userNo,
        });
      }
    } catch (e) {
      console.error('대표 이미지 자동 설정 실패:', e);
      // 실패해도 플로우는 계속 진행 (알림만 로그)
    } finally {
      setAutoFetchingImages(false);
    }
  };

  const handleNext = async () => {
    // Step-specific next handlers
    if (step === 0) {
      if (!form1Valid) return;
      // 이미 상품을 만든 뒤 [이전]으로 돌아온 경우: 재생성하지 않고 그대로 진행.
      // 수정한 1단계 값은 2단계 완료 시 saveStep2BasicSettings가 다시 저장한다.
      if (productNo) { setStep(1); return; }
      if (creatingProductRef.current) return;
      creatingProductRef.current = true;
      // Create product
      try {
        setLoadingOptions(true);
        const runtimeDefaults = getDefaultsFromToken();
        const companyNo = Number(runtimeDefaults.companyNo) || Number(defaults.companyNo);
        const userNo = Number(runtimeDefaults.userNo) || Number(defaults.userNo);
        const supplierNo = Number(runtimeDefaults.supplierNo) || Number(defaults.supplierNo);
        if (!companyNo || !userNo) {
          alert('로그인 정보 확인이 필요합니다. 페이지를 새로고침하거나 다시 로그인해 주세요.');
          return;
        }
        const durationValue = String(Number(form1.durationValue) || 0);
        const result = await addTourProduct({
          supplierNo,
          userNo,
          companyNo,
          productName: String(form1.productName || '').trim(),
          cityNo: form1.cityNo,
          durationValue,
          durationUnitCode: form1.durationUnitCode,
          currencyCode: form1.currencyCode,
          productTypeCode: form1.productTypeCode,
        });
        // Try to detect new product number from common API shape
        let newProductNo = null;
        if (result && Array.isArray(result.table) && result.table.length) {
          const first = result.table[0];
          newProductNo = first.상품번호 || first.productNo || first.번호 || null;
        }
        if (!newProductNo && result && result.table1 && Array.isArray(result.table1) && result.table1.length) {
          const first = result.table1[0];
          newProductNo = first.상품번호 || first.productNo || first.번호 || null;
        }
        if (!newProductNo) {
          // Fallback: attempt to read from any numeric field
          const candidate = (result && (result.table || result.table1 || []))[0] || {};
          const foundKey = Object.keys(candidate).find((k) => /상품번호|productNo|번호/i.test(k));
          newProductNo = foundKey ? candidate[foundKey] : null;
        }
        if (!newProductNo) {
          alert('상품 생성은 성공했을 수 있으나, 상품번호를 확인하지 못했습니다. 목록에서 확인해주세요.');
        } else {
          setProductNo(String(newProductNo));
        }
        setStep(1);
      } catch (e) {
        console.error('상품 생성 실패:', e);
        const status = e?.status;
        const msg = String(e?.apiMessage || e?.message || '');
        if (status === 401 || status === 403) {
          alert('로그인이 만료되었거나 권한이 없습니다. 다시 로그인 후 시도해주세요.');
        } else if (msg) {
          alert(`상품 생성에 실패했습니다.\n${msg}`);
        } else {
          alert('상품 생성에 실패했습니다. 입력값을 확인하고 다시 시도해주세요.');
        }
      } finally {
        setLoadingOptions(false);
        creatingProductRef.current = false;
      }
      return;
    }

    if (step === 1) {
      // 2단계 완료 시: 기본 설정 저장 후 바로 가격/재고 화면으로 이동
      try {
        await saveStep2BasicSettings();
        await autoSetupRepresentativeImages();
        if (productNo) {
          navigate(`/price-stock/${productNo}`);
        }
      } catch (e) {
        // already handled inside saveStep2BasicSettings
      }
      return;
    }
  };

  const handleBack = () => {
    if (step === 0) {
      navigate('/products');
    } else {
      setStep((s) => Math.max(0, s - 1));
    }
  };

  return (
    <div
      className="max-w-6xl mx-auto px-4 py-8"
      data-tour-screen={step === 0 ? 'quick-wizard-basic' : 'quick-wizard-smart'}
    >
      {/* Header & Stepper */}
      <div className="mb-12" data-tour="qw-stepper">
        <StepIndicator current={step} total={totalSteps} steps={stepTitles} />
      </div>

      <div className="pb-28">
        {step === 0 && (
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h1 className="text-2xl font-bold text-gray-900 mb-3">상품 기초 정보 입력</h1>

            </div>

            <div className="bg-white rounded-2xl shadow-xl border border-gray-100">
              <div className="p-8 space-y-8">
                {/* Product Name */}
                <div data-tour="qw-name">
                  <label className="block text-sm font-bold text-gray-900 mb-2">
                    상품명 <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-50 transition-all text-lg placeholder-gray-400"
                      value={form1.productName}
                      onChange={(e) => setForm1((prev) => ({ ...prev, productName: e.target.value }))}
                      onBlur={analyzeNameAndSuggest}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); analyzeNameAndSuggest(); } }}
                      placeholder="예: [오사카] 유니버셜 스튜디오 2박 3일 자유여행"
                    />
                    <div className="absolute right-3 top-3 text-gray-400">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </div>
                  </div>

                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* City Selection */}
                  <div data-tour="qw-city">
                    <label className="block text-sm font-bold text-gray-900 mb-2">
                      여행 도시 <span className="text-red-500">*</span>
                    </label>
                    <div className="relative" ref={cityBoxRef}>
                      <button
                        type="button"
                        className={`w-full px-4 py-3 text-left rounded-xl border transition-all flex items-center justify-between ${form1.cityNo
                          ? 'bg-blue-50 border-blue-200 text-blue-900'
                          : 'bg-white border-gray-200 text-gray-400 hover:border-gray-300'
                          }`}
                        onClick={() => setCityOpen((o) => !o)}
                      >
                        <span className={form1.cityNo ? 'font-medium' : ''}>
                          {(() => {
                            const current = (options.cities || []).find(c => String(c.코드 || c.code) === String(form1.cityNo));
                            return current ? String(current.코드명 || current.name) : '도시를 선택해주세요';
                          })()}
                        </span>
                        <svg className={`w-5 h-5 transition-transform ${cityOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {cityOpen && (
                        <div className="absolute z-50 mt-2 w-full bg-white border border-gray-100 rounded-xl shadow-2xl overflow-hidden animate-fadeIn">
                          <div className="p-3 bg-gray-50 border-b border-gray-100">
                            <div className="relative">
                              <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                              </svg>
                              <input
                                autoFocus
                                type="text"
                                value={cityQuery}
                                onChange={(e) => setCityQuery(e.target.value)}
                                placeholder="도시명 또는 초성 검색"
                                className="w-full pl-9 pr-4 py-2 rounded-lg border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm"
                              />
                            </div>
                          </div>
                          <ul className="max-h-60 overflow-y-auto">
                            {filteredCities.length === 0 ? (
                              <li className="px-4 py-8 text-center text-gray-500 text-sm">
                                검색 결과가 없습니다
                              </li>
                            ) : (
                              filteredCities.map((opt) => (
                                <li
                                  key={String(opt.코드 || opt.code)}
                                  className={`px-4 py-3 text-sm cursor-pointer transition-colors flex items-center justify-between ${String(opt.코드 || opt.code) === String(form1.cityNo)
                                    ? 'bg-blue-50 text-blue-700 font-medium'
                                    : 'hover:bg-gray-50 text-gray-700'
                                    }`}
                                  onClick={() => {
                                    setForm1((prev) => ({ ...prev, cityNo: String(opt.코드 || opt.code) }));
                                    setCityOpen(false);
                                  }}
                                >
                                  <span>{String(opt.코드명 || opt.name)}</span>
                                  {String(opt.코드 || opt.code) === String(form1.cityNo) && (
                                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  )}
                                </li>
                              ))
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Product Type Selection */}
                  <div data-tour="qw-type">
                    <label className="block text-sm font-bold text-gray-900 mb-2">
                      상품 타입 <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-50 transition-all appearance-none bg-white"
                        value={form1.productTypeCode}
                        onChange={(e) => setForm1((prev) => ({ ...prev, productTypeCode: e.target.value }))}
                      >
                        <option value="">선택해주세요</option>
                        {options.productTypes.map((t) => (
                          <option key={t.코드 || t.code} value={t.코드 || t.code}>
                            {t.코드명 || t.name}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-4 top-3.5 pointer-events-none text-gray-500">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-tour="qw-duration">
                  {/* Duration */}
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-2">
                      소요 시간/기간
                    </label>
                    <div className="flex gap-3">
                      <div className="flex-1">
                        <input
                          type="number"
                          min={0}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-50 transition-all"
                          value={form1.durationValue}
                          onChange={(e) => setForm1((prev) => ({ ...prev, durationValue: e.target.value }))}
                        />
                      </div>
                      <div className="w-32">
                        <select
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-50 transition-all bg-white"
                          value={form1.durationUnitCode}
                          onChange={(e) => setForm1((prev) => ({ ...prev, durationUnitCode: e.target.value }))}
                        >
                          <option value="012001">일</option>
                          <option value="012002">시간</option>
                                                  </select>
                      </div>
                    </div>
                  </div>

                  {/* Currency */}
                  <div>
                    <label className="block text-sm font-bold text-gray-900 mb-2">
                      통화 <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-50 transition-all appearance-none bg-white"
                        value={form1.currencyCode}
                        onChange={(e) => setForm1((prev) => ({ ...prev, currencyCode: e.target.value }))}
                      >
                        <option value="">선택해주세요</option>
                        {options.currencies.map((cur) => (
                          <option key={cur.코드 || cur.code} value={cur.코드 || cur.code}>
                            {cur.코드명 || cur.name}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-4 top-3.5 pointer-events-none text-gray-500">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}


        {step === 1 && (
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h1 className="text-2xl font-bold text-gray-900 mb-3">스마트 등록</h1>

            </div>

            {!productNo && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center text-red-700">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                상품번호가 생성되지 않았습니다. 처음 단계부터 다시 진행해주세요.
              </div>
            )}

            <div className="space-y-6">
              {/* Basic Settings Card */}
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100" data-tour="qw-basic-settings">
                <div className="p-8">
                  <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center">
                    <span className="w-1 h-6 bg-blue-500 rounded-full mr-3"></span>
                    기본 설정
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">
                        노출 여부
                      </label>
                      <div className="relative">
                        <select
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-50 transition-all appearance-none bg-white"
                          value={form2.expose ? 'true' : 'false'}
                          onChange={(e) => setForm2((prev) => ({ ...prev, expose: e.target.value === 'true' }))}
                        >
                          <option value="true">노출</option>
                          <option value="false">비노출</option>
                        </select>
                        <div className="absolute right-4 top-3.5 pointer-events-none text-gray-500">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-900 mb-2">
                        키워드 <span className="text-gray-400 font-normal">(쉼표로 구분)</span>
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-50 transition-all placeholder-gray-400"
                        value={form2.keywords}
                        onChange={(e) => setForm2((prev) => ({ ...prev, keywords: e.target.value }))}
                        placeholder="예: 오사카, 유니버셜, 2박3일"
                      />
                    </div>
                  </div>

                  <div className="mt-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div>
                        <label className="block text-sm font-bold text-gray-900 mb-2">상품코드</label>
                        <input
                          type="text"
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-50 transition-all placeholder-gray-400"
                          value={form2.productCode}
                          onChange={(e) => setForm2((prev) => ({ ...prev, productCode: e.target.value }))}
                          placeholder="비워두면 규칙에 따라 자동 생성됩니다"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-bold text-gray-900 mb-2">상품뱃지</label>
                        <input
                          type="text"
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-50 transition-all placeholder-gray-400"
                          value={form2.badge}
                          onChange={(e) => setForm2((prev) => ({ ...prev, badge: e.target.value }))}
                          placeholder="상품 목록에 표시할 뱃지 텍스트"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Smart Itinerary Card */}
              {productNo && (
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100" data-tour="qw-smart-itinerary">
                  <div className="p-8">
                    <h3 className="text-lg font-bold text-gray-900 mb-6 flex items-center relative group">
                      <span className="w-1 h-6 bg-indigo-500 rounded-full mr-3"></span>
                      일정 스마트 등록
                      <button
                        type="button"
                        className="ml-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border border-indigo-100"
                        onMouseEnter={() => setSmartInfoOpen(true)}
                        onMouseLeave={() => setSmartInfoOpen(false)}
                        onFocus={() => setSmartInfoOpen(true)}
                        onBlur={() => setSmartInfoOpen(false)}
                        aria-label="스마트 등록 설명 보기"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                      {smartInfoOpen && (
                        <div className="absolute left-0 top-full mt-2 w-[320px] rounded-xl border border-gray-200 bg-white shadow-xl p-4 text-sm text-gray-700 leading-relaxed z-10">
                          <p className="font-semibold text-gray-900 mb-1">2단계 안내</p>
                          <p className="mb-1">구역 선택에서 <strong>가격/재고</strong>를 고르면 스마트 가격 등록과 바로 연계됩니다.</p>
                          <p className="text-gray-600">스마트 가격 등록에서는 일정표에서 변환한 가격 정보를 분석하여 자동으로 일괄 등록할 수 있습니다.</p>
                        </div>
                      )}
                    </h3>
                    <div className="bg-gray-50 rounded-xl p-1">
                      <SmartItineraryRegisterModal
                        ref={smartModalRef}
                        mode="inline"
                        open={true}
                        onClose={() => { }}
                        productNo={Number(productNo)}
                        contentTypes={contentTypes}
                        defaultItineraryContentTypeName={"일정소개"}
                        onCompleted={async (payload) => {
                          try {
                            await saveStep2BasicSettings();
                            await autoSetupRepresentativeImages();
                            if (productNo) {
                              const priceStockNumMap = payload?.priceStockNumMap;
                              navigate(`/price-stock/${productNo}`, {
                                state: (priceStockNumMap && Object.keys(priceStockNumMap).length)
                                  ? { smartPriceStockNumMap: priceStockNumMap }
                                  : undefined,
                              });
                            }
                          } catch (e) {
                            // already handled inside saveStep2BasicSettings / autoSetupRepresentativeImages
                          }
                        }}
                        onTabChange={(tab) => setSmartModalTab(tab)}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

      </div>

      {/* Floating controls */}
      {
        (() => {
          // Step 2 (index 1): Show smart modal buttons instead of next
          if (step === 1) {
            const prevLabel = '이전';
            const isEditorTab = smartModalTab === 'editor';
            const isReviewTab = smartModalTab === 'review';

            return (
              <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-4xl">
                <div className="bg-white border rounded-lg shadow-lg px-4 py-3 flex items-center justify-between">
                  <div className="text-xs text-gray-500">단계 {step + 1} / {totalSteps}</div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                      onClick={handleBack}
                    >{prevLabel}</button>
                    <button
                      type="button"
                      className="px-6 py-2 rounded-lg font-medium text-white shadow-sm bg-gray-700 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleNext}
                      disabled={savingStep2}
                      data-tour="qw-save-basic"
                    >{savingStep2 ? '저장중...' : '기본 설정 저장'}</button>

                    {isEditorTab && (
                      <button
                        type="button"
                        className="px-6 py-2 rounded-lg font-medium text-white shadow-sm bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => smartModalRef.current?.handleConvert()}
                        disabled={smartModalRef.current?.isConvertLoading()}
                        data-tour="qw-convert"
                      >
                        {smartModalRef.current?.isConvertLoading() ? '변환중...' : '일정표 전환'}
                      </button>
                    )}

                    {isReviewTab && (
                      <button
                        type="button"
                        className="px-6 py-2 rounded-lg font-medium text-white shadow-sm bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={() => smartModalRef.current?.handleRegister()}
                        disabled={!smartModalRef.current?.hasItineraryHtml() || smartModalRef.current?.isRegisterLoading()}
                      >
                        {smartModalRef.current?.isRegisterLoading() ? '저장중...' : '일정 등록'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          }

          // Other steps: Show normal next/prev buttons (2-step wizard)
          const nextDisabled = (step === 0) ? (!form1Valid || loadingOptions) : false;
          const nextLabel = (step === 0)
            ? (loadingOptions ? '생성 중...' : '다음 단계')
            : '완료';
          const prevLabel = step === 0 ? '취소' : '이전';
          return (
            <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-4xl">
              <div className="bg-white border rounded-lg shadow-lg px-4 py-3 flex items-center justify-between">
                <div className="text-xs text-gray-500">단계 {step + 1} / {totalSteps}</div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                    onClick={handleBack}
                    disabled={autoFetchingImages}
                  >{prevLabel}</button>
                  <button
                    type="button"
                    className={`px-6 py-2 rounded-lg font-medium text-white shadow-sm ${nextDisabled ? 'bg-gray-300 cursor-not-allowed' : 'bg-gray-700 hover:bg-gray-800'}`}
                    onClick={handleNext}
                    disabled={nextDisabled}
                    data-tour="qw-next"
                  >{nextLabel}</button>
                </div>
              </div>
            </div>
          );
        })()
      }

      {/* Landbox 일정표 선택 모달 (Step 4) */}
      {
        landboxOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => setLandboxOpen(false)} />
            <div className="relative z-10 w-[900px] max-w-[96vw] max-h-[88vh] bg-white rounded shadow-lg flex flex-col">
              <div className="px-4 py-3 border-b flex items-center justify-between">
                <h4 className="text-sm font-semibold">랜드박스 일정표 선택</h4>
                <button className="text-sm text-gray-500 hover:text-gray-800" onClick={() => setLandboxOpen(false)}>닫기</button>
              </div>
              <div className="p-3">
                {landboxLoading && <div className="text-xs text-gray-500">불러오는 중...</div>}
                {!landboxLoading && landboxError && <div className="text-xs text-red-600">{landboxError}</div>}
                {!landboxLoading && !landboxError && (
                  <div className="overflow-auto max-h-[60vh] border rounded">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-[12px] text-gray-600 text-center">일정번호</th>
                          <th className="px-3 py-2 text-[12px] text-gray-600 text-center">일정명</th>
                          <th className="px-3 py-2 text-[12px] text-gray-600 text-center">상품가</th>
                          <th className="px-3 py-2 text-[12px] text-gray-600 text-center">방문도시</th>
                          <th className="px-3 py-2 text-[12px] text-gray-600 text-center w-28">선택</th>
                        </tr>
                        <tr className="border-t border-gray-200">
                          <th className="px-2 py-1">
                            <input
                              type="text"
                              value={landboxFilters.no}
                              onChange={(e) => setLandboxFilters((p) => ({ ...p, no: e.target.value }))}
                              placeholder="검색"
                              className="w-full px-2 py-1 text-[11px] border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </th>
                          <th className="px-2 py-1">
                            <input
                              type="text"
                              value={landboxFilters.name}
                              onChange={(e) => setLandboxFilters((p) => ({ ...p, name: e.target.value }))}
                              placeholder="검색"
                              className="w-full px-2 py-1 text-[11px] border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </th>
                          <th className="px-2 py-1">
                            <input
                              type="text"
                              value={landboxFilters.price}
                              onChange={(e) => setLandboxFilters((p) => ({ ...p, price: e.target.value }))}
                              placeholder="검색"
                              className="w-full px-2 py-1 text-[11px] border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </th>
                          <th className="px-2 py-1">
                            <input
                              type="text"
                              value={landboxFilters.city}
                              onChange={(e) => setLandboxFilters((p) => ({ ...p, city: e.target.value }))}
                              placeholder="검색"
                              className="w-full px-2 py-1 text-[11px] border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                          </th>
                          <th className="px-2 py-1 text-center">
                            <button
                              type="button"
                              className="text-[11px] px-2 py-1 border rounded bg-white hover:bg-gray-50"
                              onClick={() => setLandboxFilters({ no: '', name: '', price: '', city: '' })}
                            >초기화</button>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {Array.isArray(filteredLandboxList) && filteredLandboxList.length > 0 ? filteredLandboxList.map((r) => (
                          <tr key={`lbx-${r.일정번호}`} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-xs text-gray-700 text-center">{r.일정번호}</td>
                            <td className="px-3 py-2 text-xs text-gray-900 text-center">{r.일정명}</td>
                            <td className="px-3 py-2 text-xs text-gray-700 text-center">{r.상품가 || ''}</td>
                            <td className="px-3 py-2 text-xs text-gray-700 text-center">{r.방문도시 || ''}</td>
                            <td className="px-3 py-2 text-xs text-center w-28">
                              <button
                                type="button"
                                className="inline-flex items-center justify-center whitespace-nowrap min-w-[80px] px-3 py-1.5 text-xs rounded border bg-white hover:bg-gray-50"
                                onClick={() => handlePickLandboxSchedule(r.일정번호)}
                              >가져오기</button>
                            </td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan={5} className="px-3 py-6 text-center text-xs text-gray-500">데이터가 없습니다.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      }

    </div>
  );
};

export default QuickProductWizard;
